/* ============================================
   HYDE Store Locator Section Scripts
   ============================================ */

(function () {
  "use strict";

  if (window.__hydeSLBooted) return;
  window.__hydeSLBooted = true;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const observerOptions = {
    threshold: 0.05,
    rootMargin: "0px 0px 50px 0px",
  };

  function getAnime() {
    if (!window.anime) return null;
    if (typeof window.anime === "function") return window.anime;
    if (typeof window.anime.animate === "function") return window.anime;
    return null;
  }

  function runAnime(anime, options) {
    if (!anime) return null;
    if (typeof anime === "function") return anime(options);
    const { targets, ...params } = options;
    return anime.animate(targets, params);
  }

  function createTimeline(anime, options) {
    if (!anime) return null;
    if (typeof anime === "function" && typeof anime.timeline === "function") {
      return anime.timeline(options || {});
    }
    if (typeof anime.createTimeline === "function") {
      return anime.createTimeline(options || {});
    }
    return null;
  }

  function getStagger(anime, value, options) {
    if (!anime || typeof anime.stagger !== "function") return 0;
    return anime.stagger(value, options || {});
  }

  function isAnimated(el) {
    return Boolean(el) && el.dataset.hydeAnimated === "true";
  }

  function markAnimated(el) {
    if (!el) return;
    el.dataset.hydeAnimated = "true";
  }

  function clearAnimatedStyles(el) {
    if (!el) return;
    el.style.opacity = "";
    el.style.transform = "";
    el.style.willChange = "";
  }

  function finalizeAnimation(el) {
    if (!el) return;
    markAnimated(el);
    clearAnimatedStyles(el);
  }

  function getTargets(scope, selector) {
    const targets = [];

    if (scope instanceof Element && scope.matches(selector)) {
      targets.push(scope);
    }

    if (!scope || !scope.querySelectorAll) {
      return targets;
    }

    return targets.concat(Array.from(scope.querySelectorAll(selector)));
  }

  function getStaggerChildren(container, childSelector) {
    if (!container) return [];

    if (childSelector) {
      return Array.from(container.querySelectorAll(childSelector));
    }

    return Array.from(container.children);
  }

  function revealImmediately(el) {
    if (!el || isAnimated(el)) return;
    finalizeAnimation(el);
  }

  function revealStaggerImmediately(container, childSelector) {
    if (!container || isAnimated(container)) return;

    markAnimated(container);
    getStaggerChildren(container, childSelector).forEach(clearAnimatedStyles);
  }

  function animateReveal(el, options) {
    if (!el || isAnimated(el)) return;

    if (prefersReducedMotion) {
      revealImmediately(el);
      return;
    }

    const anime = getAnime();
    if (!anime) {
      revealImmediately(el);
      return;
    }

    el.style.willChange = "opacity, transform";

    runAnime(anime, {
      targets: el,
      opacity: [0, 1],
      translateY: [options && options.distance ? options.distance : 24, 0],
      duration: options && options.duration ? options.duration : 860,
      delay: options && options.delay ? options.delay : 0,
      easing: options && options.easing ? options.easing : "outElastic",
      complete: function () {
        finalizeAnimation(el);
      },
    });
  }

  function animateStagger(container, childSelector, options) {
    if (!container || isAnimated(container)) return;

    const children = getStaggerChildren(container, childSelector);

    if (!children.length) {
      markAnimated(container);
      return;
    }

    if (prefersReducedMotion) {
      revealStaggerImmediately(container, childSelector);
      return;
    }

    const anime = getAnime();
    if (!anime) {
      revealStaggerImmediately(container, childSelector);
      return;
    }

    children.forEach((child) => {
      child.style.willChange = "opacity, transform";
    });

    runAnime(anime, {
      targets: children,
      opacity: [0, 1],
      translateY: [options && options.distance ? options.distance : 18, 0],
      duration: options && options.duration ? options.duration : 640,
      delay: getStagger(
        anime,
        options && options.stagger ? options.stagger : 80,
        {
          start: options && options.delay ? options.delay : 0,
        },
      ),
      easing: options && options.easing ? options.easing : "outElastic",
      complete: function () {
        markAnimated(container);
        children.forEach(clearAnimatedStyles);
      },
    });
  }

  function animateCounters(container) {
    const anime = getAnime();
    const counters = container.querySelectorAll(".hyde-sl__stat-number");

    counters.forEach((counter) => {
      if (counter.dataset.hydeCounted === "true") return;

      if (counter.dataset.display) {
        counter.dataset.hydeCounted = "true";
        return;
      }

      const target = parseInt(counter.dataset.target, 10);
      if (Number.isNaN(target)) {
        counter.dataset.hydeCounted = "true";
        return;
      }

      const suffix = counter.dataset.suffix || "";
      const finalValue = target.toLocaleString() + suffix;

      if (prefersReducedMotion || !anime) {
        counter.textContent = finalValue;
        counter.dataset.hydeCounted = "true";
        return;
      }

      const counterValue = { value: 0 };
      counter.dataset.hydeCounted = "true";
      counter.textContent = "0" + suffix;

      runAnime(anime, {
        targets: counterValue,
        value: target,
        round: 1,
        duration: 1800,
        easing: "easeOutCubic",
        update: function () {
          counter.textContent = counterValue.value.toLocaleString() + suffix;
        },
        complete: function () {
          counter.textContent = finalValue;
        },
      });
    });
  }

  const observer =
    "IntersectionObserver" in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const target = entry.target;

            if (
              target.dataset.hydeAnimation === "stagger" ||
              target.classList.contains("hyde-mkt__anim-stagger")
            ) {
              animateStagger(target, target.dataset.hydeChildSelector || "", {
                distance: 18,
                duration: 640,
                stagger: 80,
              });
            } else {
              animateReveal(target, {
                distance: 24,
                duration: 860,
              });
            }

            if (target.dataset.hydeCountup === "true") {
              animateCounters(target);
            }

            observer.unobserve(target);
          });
        }, observerOptions)
      : null;

  function queueReveal(target, config) {
    if (!target || isAnimated(target)) return;

    const animationType = config && config.type ? config.type : "reveal";

    if (animationType === "stagger") {
      if (!target.classList.contains("hyde-mkt__anim-stagger")) {
        target.classList.add("hyde-anim-stagger-init");
      }
      target.dataset.hydeAnimation = "stagger";

      if (config && config.childSelector) {
        target.dataset.hydeChildSelector = config.childSelector;
      }
    } else {
      if (!target.classList.contains("hyde-mkt__anim")) {
        target.classList.add("hyde-anim-init");
      }
      target.dataset.hydeAnimation = "reveal";
    }

    if (config && config.countup) {
      target.dataset.hydeCountup = "true";
    }

    if (prefersReducedMotion || !observer) {
      if (
        animationType === "stagger" ||
        target.classList.contains("hyde-mkt__anim-stagger")
      ) {
        animateStagger(
          target,
          config && config.childSelector ? config.childSelector : "",
          {
            distance: 18,
            duration: 640,
            stagger: 80,
          },
        );
      } else {
        animateReveal(target, {
          distance: 24,
          duration: 860,
        });
      }

      if (config && config.countup) {
        animateCounters(target);
      }

      return;
    }

    observer.observe(target);
  }

  function initHeroAnimations(wrapper) {
    const heroElements = [
      wrapper.querySelector(".hyde-sl__hero-label"),
      wrapper.querySelector(".hyde-sl__hero-title"),
      wrapper.querySelector(".hyde-sl__hero-subtitle"),
    ].filter((el) => el && !isAnimated(el));

    if (!heroElements.length) return;

    heroElements.forEach((el) => {
      el.classList.add("hyde-anim-init");
    });

    if (prefersReducedMotion) {
      heroElements.forEach(revealImmediately);
      return;
    }

    const anime = getAnime();
    if (!anime) {
      heroElements.forEach(revealImmediately);
      return;
    }

    heroElements.forEach((el) => {
      el.style.willChange = "opacity, transform";
    });

    const timeline = createTimeline(anime, {
      autoplay: true,
    });

    if (!timeline || typeof timeline.add !== "function") {
      heroElements.forEach((el, index) => {
        runAnime(anime, {
          targets: el,
          opacity: [0, 1],
          translateY: [28, 0],
          delay: 140 + index * 140,
          duration: 960,
          easing: "outElastic",
          complete: function () {
            finalizeAnimation(el);
          },
        });
      });
      return;
    }

    timeline.add({
      targets: heroElements,
      opacity: [0, 1],
      translateY: [28, 0],
      delay: getStagger(anime, 140, { start: 140 }),
      duration: 960,
      easing: "outElastic",
      complete: function () {
        heroElements.forEach(finalizeAnimation);
      },
    });
  }

  function initScrollAnimations(wrapper) {
    [
      wrapper.querySelector(".hyde-sl__locator-inner"),
      wrapper.querySelector(".hyde-sl__retailers-title"),
      wrapper.querySelector(".hyde-sl__retailers-subtitle"),
      wrapper.querySelector(".hyde-sl__cta-inner"),
      wrapper.querySelector(".hyde-sl__stats-header"),
      wrapper.querySelector(".hyde-mkt__timeline-header"),
      wrapper.querySelector(".hyde-mkt__principles-header"),
      wrapper.querySelector(".hyde-mkt__brand-story-text"),
      wrapper.querySelector(".hyde-mkt__science-top"),
      wrapper.querySelector(".hyde-mkt__science-bottom"),
      wrapper.querySelector(".hyde-mkt__faq-header"),
    ].forEach((target) => {
      if (target) queueReveal(target);
    });

    queueReveal(wrapper.querySelector(".hyde-sl__retailers-grid"), {
      type: "stagger",
      childSelector: ".hyde-sl__retailer-card",
    });

    queueReveal(wrapper.querySelector(".hyde-sl__stats-inner"), {
      countup: true,
    });
  }

  function initMarketingAnimations(wrapper) {
    wrapper.querySelectorAll(".hyde-mkt__anim").forEach((target) => {
      queueReveal(target);
    });

    wrapper.querySelectorAll(".hyde-mkt__anim-stagger").forEach((target) => {
      queueReveal(target, {
        type: "stagger",
      });
    });
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function toRadians(value) {
    return value * (Math.PI / 180);
  }

  function getDistanceMiles(fromLat, fromLng, toLat, toLng) {
    const earthRadiusMiles = 3958.8;
    const deltaLat = toRadians(toLat - fromLat);
    const deltaLng = toRadians(toLng - fromLng);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRadians(fromLat)) *
        Math.cos(toRadians(toLat)) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);

    return earthRadiusMiles * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  function parseMiles(label) {
    const match = (label || "").match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  }

  function normalizeValue(value) {
    return (value || "").toString().trim().toLowerCase();
  }

  function formatMiles(value) {
    if (typeof value !== "number" || Number.isNaN(value)) return "";
    if (value < 10) return `${value.toFixed(1)} miles`;
    return `${Math.round(value)} miles`;
  }

  function geocodeUsZip(query) {
    const trimmedQuery = (query || "").trim();

    if (!trimmedQuery) {
      return Promise.resolve(null);
    }

    const zipMatch = trimmedQuery.match(/^\d{5}(?:-\d{4})?$/);
    const searchTerm = encodeURIComponent(trimmedQuery);

    if (zipMatch) {
      return fetch(`https://api.zippopotam.us/us/${searchTerm}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((payload) => {
          if (!payload || !payload.places || !payload.places.length) {
            return null;
          }

          const place = payload.places[0];
          return {
            lat: parseFloat(place.latitude),
            lng: parseFloat(place.longitude),
            label: trimmedQuery,
          };
        })
        .catch(() => null)
        .then((result) => {
          if (result) return result;

          return fetch(
            `https://nominatim.openstreetmap.org/search?countrycodes=us&postalcode=${searchTerm}&format=jsonv2&limit=1`,
          )
            .then((response) => (response.ok ? response.json() : []))
            .then((payload) => {
              if (!payload || !payload.length) return null;

              return {
                lat: parseFloat(payload[0].lat),
                lng: parseFloat(payload[0].lon),
                label: trimmedQuery,
              };
            })
            .catch(() => null);
        });
    }

    return fetch(
      `https://nominatim.openstreetmap.org/search?q=${searchTerm}&countrycodes=us&format=jsonv2&limit=1`,
    )
      .then((response) => (response.ok ? response.json() : []))
      .then((payload) => {
        if (!payload || !payload.length) return null;

        return {
          lat: parseFloat(payload[0].lat),
          lng: parseFloat(payload[0].lon),
          label: trimmedQuery,
        };
      })
      .catch(() => null);
  }

  function initCustomSelect(locator, nativeSelect) {
    if (!locator || !nativeSelect) return;

    const root = locator.querySelector("[data-select-root]");
    if (!root || root.dataset.hydeSelectReady === "true") return;

    const trigger = root.querySelector("[data-select-trigger]");
    const valueEl = root.querySelector("[data-select-value]");
    const menu = root.querySelector("[data-select-menu]");
    if (!trigger || !valueEl || !menu) return;

    const options = Array.from(
      menu.querySelectorAll(".hyde-sl__select-option"),
    );
    if (!options.length) return;

    root.dataset.hydeSelectReady = "true";

    function syncFromNative() {
      const currentValue = nativeSelect.value || "";
      let matched = null;
      options.forEach((opt) => {
        const isMatch = (opt.dataset.value || "") === currentValue;
        opt.classList.toggle("is-selected", isMatch);
        opt.setAttribute("aria-selected", isMatch ? "true" : "false");
        if (isMatch) matched = opt;
      });
      if (!matched) matched = options[0];
      valueEl.textContent = matched.textContent.trim();
    }

    function openMenu() {
      menu.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      document.addEventListener("mousedown", onDocClick, true);
      document.addEventListener("keydown", onKeydown, true);
    }

    function closeMenu() {
      menu.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      document.removeEventListener("mousedown", onDocClick, true);
      document.removeEventListener("keydown", onKeydown, true);
    }

    function toggleMenu() {
      if (menu.hidden) openMenu();
      else closeMenu();
    }

    function selectOption(opt) {
      const nextValue = opt.dataset.value || "";
      if (nativeSelect.value !== nextValue) {
        nativeSelect.value = nextValue;
        nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
      syncFromNative();
      closeMenu();
      trigger.focus();
    }

    function onDocClick(event) {
      if (root.contains(event.target)) return;
      closeMenu();
    }

    function onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        trigger.focus();
      }
    }

    trigger.addEventListener("click", function (event) {
      event.preventDefault();
      toggleMenu();
    });

    options.forEach((opt) => {
      opt.addEventListener("click", function () {
        selectOption(opt);
      });
    });

    nativeSelect.addEventListener("change", syncFromNative);

    syncFromNative();
  }

  function initStoreLocator(wrapper) {
    const locator = wrapper.querySelector("[data-hyde-store-locator]");
    if (!locator || locator.dataset.hydeLocatorReady === "true") return;

    locator.dataset.hydeLocatorReady = "true";

    const searchInput = locator.querySelector("[data-search-input]");
    const searchButton = locator.querySelector("[data-search-button]");
    const productFilter = locator.querySelector("[data-product-filter]");
    const resultsList = locator.querySelector("[data-results-list]");
    const resultsLabel = locator.querySelector("[data-results-label]");
    const emptyResults = locator.querySelector("[data-results-empty]");
    const searchRadius = parseFloat(locator.dataset.searchRadius || "3000");
    const maxResults = Math.max(
      1,
      parseInt(locator.dataset.maxResults || "3", 10) || 3,
    );
    const autoLocate = locator.dataset.autoLocate !== "false";
    let defaultProduct = normalizeValue(locator.dataset.defaultProduct);
    if (defaultProduct === "all") defaultProduct = "";
    const filterAllLabel = locator.dataset.filterAllLabel || "All products";
    const resultsSuffix = locator.dataset.resultsSuffix || "Locations found";
    const searchErrorText =
      locator.dataset.searchErrorText || "We could not find that ZIP code.";
    const emptyResultsText =
      locator.dataset.emptyResultsText || "No stores found for this search.";
    let activeStoreIndex = 0;
    let currentLocation = null;

    // ─── Leaflet map state ───────────────────────────────────────
    const mapContainer = locator.querySelector("[data-leaflet-map]");
    let leafletMap = null;
    let leafletMarkers = {};
    let userMarker = null;

    function initLeafletMap() {
      if (!window.L || !mapContainer || leafletMap) return;

      leafletMap = L.map(mapContainer, {
        zoomControl: true,
        scrollWheelZoom: true,
      });

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 19,
        },
      ).addTo(leafletMap);

      stores.forEach(function (store) {
        leafletMarkers[store.index] = createLeafletMarker(store);
      });

      fitMapToStores(stores);
    }

    function createLeafletMarker(store) {
      if (!leafletMap) return null;

      const icon = L.divIcon({
        html:
          '<div class="hyde-sl__leaflet-pin' +
          (store.index === 0 ? " is-active" : "") +
          '"></div>',
        className: "",
        iconSize: [21, 21],
        iconAnchor: [10, 10],
      });

      const marker = L.marker([store.lat, store.lng], { icon }).addTo(
        leafletMap,
      );

      marker.on("click", function () {
        setActiveStore(store);
        store.card.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });

      return marker;
    }

    function setLeafletMarkerActive(store, isActive) {
      const marker = leafletMarkers[store.index];
      const el = marker && marker.getElement();
      const pin = el && el.querySelector(".hyde-sl__leaflet-pin");
      if (pin) pin.classList.toggle("is-active", isActive);
    }

    function updateLeafletMarkerVisibility(store, visible) {
      const marker = leafletMarkers[store.index];
      if (!marker || !leafletMap) return;
      if (visible && !leafletMap.hasLayer(marker)) marker.addTo(leafletMap);
      if (!visible && leafletMap.hasLayer(marker))
        leafletMap.removeLayer(marker);
    }

    function setLeafletUserMarker(location) {
      if (!leafletMap) return;

      if (userMarker) {
        userMarker.setLatLng([location.lat, location.lng]);
        return;
      }

      const icon = L.divIcon({
        html: '<div class="hyde-sl__leaflet-pin hyde-sl__leaflet-pin--user"></div>',
        className: "",
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      userMarker = L.marker([location.lat, location.lng], {
        icon: icon,
        zIndexOffset: 1000,
      }).addTo(leafletMap);
    }

    function removeLeafletUserMarker() {
      if (userMarker && leafletMap) {
        leafletMap.removeLayer(userMarker);
        userMarker = null;
      }
    }

    function fitMapToStores(list) {
      if (!leafletMap || !list.length) return;

      if (list.length === 1) {
        leafletMap.setView([list[0].lat, list[0].lng], 14);
        return;
      }

      const bounds = L.latLngBounds(
        list.map(function (s) {
          return [s.lat, s.lng];
        }),
      );
      leafletMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }

    function flyToStore(store) {
      if (leafletMap)
        leafletMap.flyTo([store.lat, store.lng], 15, { duration: 0.6 });
    }

    function locateMe(options) {
      if (!navigator.geolocation) return;

      const silent = options && options.silent;
      const btn = locator.querySelector("[data-locate-btn]");
      if (btn && !silent) {
        btn.disabled = true;
        btn.classList.add("is-loading");
      }

      navigator.geolocation.getCurrentPosition(
        function (pos) {
          currentLocation = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };

          if (searchInput && !silent) searchInput.value = "";
          setLeafletUserMarker(currentLocation);
          renderStores("");

          if (btn && !silent) {
            btn.disabled = false;
            btn.classList.remove("is-loading");
          }
        },
        function () {
          if (btn && !silent) {
            btn.disabled = false;
            btn.classList.remove("is-loading");
          }
        },
        { timeout: 10000, maximumAge: 60000 },
      );
    }
    // ─── End Leaflet state ───────────────────────────────────────

    const stores = Array.from(locator.querySelectorAll("[data-store-card]"))
      .map((card, index) => {
        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        const rawTags = (card.dataset.tags || "")
          .split("|")
          .map((value) => value.trim())
          .filter(Boolean);
        const tagLabels = Array.from(
          card.querySelectorAll(".hyde-sl__result-tag"),
        )
          .map((tag) => tag.textContent.trim())
          .filter(Boolean);
        const distanceLabel = card.querySelector("[data-distance-label]");
        const directionsLink = card.querySelector(
          ".hyde-sl__result-directions",
        );

        return {
          index: index,
          card: card,
          lat: lat,
          lng: lng,
          tags: rawTags,
          tagLabels: tagLabels,
          distanceLabel: distanceLabel,
          directionsLink: directionsLink,
          defaultDistance: card.dataset.defaultDistance || "",
          defaultDistanceValue: parseMiles(card.dataset.defaultDistance || ""),
          directionsUrl: card.dataset.directionsUrl || "",
        };
      })
      .filter((store) => !Number.isNaN(store.lat) && !Number.isNaN(store.lng));

    const uniqueTags = [];
    stores.forEach((store) => {
      store.tags.forEach((tag, tagIndex) => {
        if (!tag || uniqueTags.some((existing) => existing.value === tag)) {
          return;
        }

        uniqueTags.push({
          value: tag,
          label: store.tagLabels[tagIndex] || tag,
        });
      });
    });

    if (productFilter) {
      const preRendered = productFilter.options.length > 1;

      if (!preRendered) {
        productFilter.innerHTML = "";

        const allOption = document.createElement("option");
        allOption.value = "";
        allOption.textContent = filterAllLabel;
        productFilter.appendChild(allOption);

        uniqueTags.forEach((tag) => {
          const option = document.createElement("option");
          option.value = tag.value;
          option.textContent = tag.label;
          productFilter.appendChild(option);
        });
      }

      if (defaultProduct) {
        productFilter.value = defaultProduct;
      }
    }

    function updateStatus(count, message) {
      if (!resultsLabel) return;

      if (message) {
        resultsLabel.textContent = message;
        return;
      }

      resultsLabel.textContent = `${count} ${resultsSuffix}`.trim();
    }

    function setActiveStore(store) {
      if (!store) return;

      activeStoreIndex = store.index;

      stores.forEach((entry) => {
        const isActive = entry.index === store.index;
        entry.card.classList.toggle("is-active", isActive);
        setLeafletMarkerActive(entry, isActive);
      });

      flyToStore(store);
    }

    function applyDirections(store) {
      if (!store || !store.directionsLink || !store.directionsUrl) return;
      store.directionsLink.href = store.directionsUrl;
    }

    function renderStores(message) {
      if (!resultsList) return;

      const selectedTag = normalizeValue(
        productFilter ? productFilter.value : "",
      );
      const visibleStores = stores.filter((store) => {
        const matchesTag = !selectedTag || store.tags.indexOf(selectedTag) > -1;

        if (!matchesTag) return false;
        if (!currentLocation) return true;

        const miles = getDistanceMiles(
          currentLocation.lat,
          currentLocation.lng,
          store.lat,
          store.lng,
        );
        store.currentDistance = miles;
        return true;
      });

      const orderedStores = visibleStores
        .slice()
        .sort((left, right) => {
          if (currentLocation) {
            return left.currentDistance - right.currentDistance;
          }

          const leftValue =
            typeof left.defaultDistanceValue === "number"
              ? left.defaultDistanceValue
              : left.index;
          const rightValue =
            typeof right.defaultDistanceValue === "number"
              ? right.defaultDistanceValue
              : right.index;
          return leftValue - rightValue;
        })
        .slice(0, maxResults);

      stores.forEach((store) => {
        const visible = orderedStores.some(
          (entry) => entry.index === store.index,
        );
        store.card.hidden = !visible;
        updateLeafletMarkerVisibility(store, visible);

        if (!currentLocation && store.distanceLabel) {
          store.distanceLabel.textContent = store.defaultDistance;
        }
      });

      orderedStores.forEach((store) => {
        resultsList.appendChild(store.card);
        applyDirections(store);

        if (currentLocation && store.distanceLabel) {
          store.distanceLabel.textContent = formatMiles(store.currentDistance);
        }
      });

      if (emptyResults) {
        emptyResults.hidden = orderedStores.length > 0;
        if (!orderedStores.length) {
          emptyResults.textContent = message || emptyResultsText;
        }
      }

      updateStatus(
        orderedStores.length,
        orderedStores.length ? "" : message || emptyResultsText,
      );

      const nextActiveStore =
        orderedStores.find((store) => store.index === activeStoreIndex) ||
        orderedStores[0];
      if (nextActiveStore) {
        setActiveStore(nextActiveStore);
        if (currentLocation) {
          fitMapToStores(orderedStores);
          setTimeout(function () {
            flyToStore(nextActiveStore);
          }, 400);
        }
      } else {
        stores.forEach((store) => {
          store.card.classList.remove("is-active");
          setLeafletMarkerActive(store, false);
        });
      }
    }

    function runSearch() {
      const query = searchInput ? searchInput.value.trim() : "";

      if (!query) {
        currentLocation = null;
        removeLeafletUserMarker();
        renderStores("");
        return Promise.resolve();
      }

      if (searchButton) {
        searchButton.disabled = true;
        searchButton.classList.add("is-loading");
      }

      return geocodeUsZip(query)
        .then((location) => {
          if (
            !location ||
            Number.isNaN(location.lat) ||
            Number.isNaN(location.lng)
          ) {
            currentLocation = null;
            removeLeafletUserMarker();
            renderStores(searchErrorText);
            return;
          }

          currentLocation = location;
          setLeafletUserMarker(location);
          renderStores("");
        })
        .catch(() => {
          currentLocation = null;
          removeLeafletUserMarker();
          renderStores(searchErrorText);
        })
        .finally(() => {
          if (searchButton) {
            searchButton.disabled = false;
            searchButton.classList.remove("is-loading");
          }
        });
    }

    stores.forEach((store) => {
      store.card.addEventListener("click", function (event) {
        if (
          event.target &&
          event.target.closest(".hyde-sl__result-directions")
        ) {
          return;
        }
        setActiveStore(store);
      });
    });

    if (productFilter) {
      productFilter.addEventListener("change", function () {
        renderStores("");
      });
    }

    initCustomSelect(locator, productFilter);

    if (searchButton) {
      searchButton.addEventListener("click", function () {
        runSearch();
      });
    }

    if (searchInput) {
      searchInput.addEventListener("keydown", function (event) {
        if (event.key !== "Enter") return;
        event.preventDefault();
        runSearch();
      });
    }

    const locateBtn = locator.querySelector("[data-locate-btn]");
    if (locateBtn) {
      locateBtn.addEventListener("click", function () {
        locateMe();
      });
    }

    renderStores("");

    if (searchInput && searchInput.value.trim()) {
      runSearch();
    }

    let mapBooted = false;
    function bootMap() {
      if (mapBooted) return;
      mapBooted = true;
      initLeafletMap();
      renderStores("");
      if (autoLocate && navigator.geolocation) {
        locateMe({ silent: true });
      }
    }

    if (window.L) {
      bootMap();
    } else {
      const leafletScript = document.querySelector('script[src*="leaflet"]');
      if (leafletScript) {
        leafletScript.addEventListener("load", bootMap, { once: true });
      }
      let tries = 0;
      const maxTries = 60;
      const pollL = setInterval(() => {
        tries += 1;
        if (window.L) {
          clearInterval(pollL);
          bootMap();
        } else if (tries >= maxTries) {
          clearInterval(pollL);
          const fallback = document.createElement("script");
          fallback.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          fallback.onload = bootMap;
          document.head.appendChild(fallback);
        }
      }, 100);
    }
  }

  function initWrapper(wrapper) {
    initHeroAnimations(wrapper);
    initScrollAnimations(wrapper);
    initMarketingAnimations(wrapper);
    initStoreLocator(wrapper);
  }

  function boot(scope) {
    getTargets(scope || document, ".hyde-sl, .hyde-pc-cards, .hyde-pc").forEach(
      (wrapper) => {
        initWrapper(wrapper);
      },
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    boot(event.target);
  });
})();
